import React from 'react';
import { useSelector } from 'react-redux';
import { TablePagination } from '../shared/table/TablePagination';
import { getSupportTickets } from '../../../../helpers/support-helper';

const STATUS_OPTIONS = [
    { value: 'ALL', label: 'Todos' },
    { value: 'OPEN', label: 'Abierto' },
    { value: 'IN_PROGRESS', label: 'En progreso' },
    { value: 'WAITING_CUSTOMER', label: 'En espera cliente' },
    { value: 'RESOLVED', label: 'Resuelto' },
    { value: 'CLOSED', label: 'Cerrado' },
];

const statusBadgeClassMap = {
    OPEN: 'bg-soft-primary text-primary',
    IN_PROGRESS: 'bg-soft-warning text-warning',
    WAITING_CUSTOMER: 'bg-soft-info text-info',
    RESOLVED: 'bg-soft-success text-success',
    CLOSED: 'bg-soft-secondary text-secondary',
};

export const SupportScreen = () => {
    const { token } = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [tickets, setTickets] = React.useState([]);

    const [statusFilter, setStatusFilter] = React.useState('ALL');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [offset, setOffset] = React.useState(0);
    const [limit, setLimit] = React.useState(10);
    const [pagination, setPagination] = React.useState({
        total: 0,
        hasNext: false,
        hasPrev: false,
    });

    const loadTickets = React.useCallback(async () => {
        if (!token) {
            setError('No se pudo validar la sesión.');
            return;
        }

        setIsLoading(true);

        const params = {
            limit,
            offset,
        };

        if (statusFilter !== 'ALL') {
            params.status = statusFilter;
        }

        const normalizedSearch = String(searchTerm || '').trim();
        if (normalizedSearch) {
            const isSupportCode = /^SUP-/i.test(normalizedSearch);
            params.field = isSupportCode ? 'trackingCode' : 'subject';
            params.value = normalizedSearch;
        }

        const resp = await getSupportTickets(token, params);
        if (!resp?.success) {
            setError(resp?.message || 'No se pudo obtener el listado de tickets de soporte.');
            setTickets([]);
            setPagination({ total: 0, hasNext: false, hasPrev: false });
            setIsLoading(false);
            return;
        }

        const rows = Array.isArray(resp.data) ? resp.data : [];
        setTickets(rows);
        setPagination({
            total: Number(resp.total || 0),
            hasNext: Boolean(resp.hasNext),
            hasPrev: Boolean(resp.hasPrev),
        });
        setIsLoading(false);
    }, [token, limit, offset, statusFilter, searchTerm]);

    React.useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    return (
        <div className="row">
            <div className="col-12">
                <div className="card stretch stretch-full card__no-border">
                    <div className="card-body pt-3">
                        {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}

                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                            <div className="d-flex gap-2">
                                <input
                                    type="search"
                                    className="form-control form-control-sm"
                                    style={{ minWidth: '220px' }}
                                    placeholder="Buscar por asunto o SUP-..."
                                    value={searchTerm}
                                    onChange={(event) => {
                                        setSearchTerm(event.target.value);
                                        setOffset(0);
                                    }}
                                />
                                <select
                                    className="form-select form-select-sm"
                                    value={statusFilter}
                                    onChange={(event) => {
                                        setStatusFilter(event.target.value);
                                        setOffset(0);
                                    }}
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <select
                                    className="form-select form-select-sm"
                                    value={limit}
                                    onChange={(event) => {
                                        setLimit(Number(event.target.value));
                                        setOffset(0);
                                    }}
                                >
                                    {[10, 25, 50].map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Seguimiento</th>
                                        <th>Asunto</th>
                                        <th>Prioridad</th>
                                        <th>Estatus</th>
                                        <th>Fecha</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading && (
                                        <tr>
                                            <td colSpan={6} className="text-center text-muted py-3">Cargando...</td>
                                        </tr>
                                    )}

                                    {!isLoading && tickets.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="text-center text-muted py-3">No hay tickets de soporte para mostrar.</td>
                                        </tr>
                                    )}

                                    {!isLoading && tickets.map((ticket) => (
                                        <tr key={ticket._id}>
                                            <td className="fw-semibold">{ticket.trackingCode}</td>
                                            <td>
                                                <div className="fw-semibold">{ticket.subject}</div>
                                                <div className="text-muted fs-12">{ticket.description}</div>
                                            </td>
                                            <td>{ticket.priority || '-'}</td>
                                            <td>
                                                <span className={`badge ${statusBadgeClassMap[ticket.status] || 'bg-soft-secondary text-secondary'}`}>
                                                    {ticket.status || '-'}
                                                </span>
                                            </td>
                                            <td>{ticket.createdDate ? new Date(ticket.createdDate).toLocaleString('es-MX') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <TablePagination
                            total={pagination.total}
                            limit={limit}
                            offset={offset}
                            hasNext={pagination.hasNext}
                            hasPrev={pagination.hasPrev}
                            onOffsetChange={setOffset}
                            isLoading={isLoading}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
